import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { Box, Checkbox, checkboxClasses } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { navigate, useLocation } from '@reach/router'
import { ROUTE_CALENDAR, ROUTE_NEWS_WALL } from '../../routes/routes-constants'
import { useTranslation } from 'react-i18next'
import { emptyEventDTO, EventDTO, fromModel } from '../../modules/calendar/models/EventDTO'
import { getCalendarContainer } from '../../container/calendar-module'
import { EventService } from '../../modules/calendar/services/EventService'
import { EVENT_SERVICE_KEY } from '../../modules/calendar'
import { CronItemMockup } from '../../components/form-card/CronItemMockup'
import { File as F } from '../../modules/files/models/File'
import { getUserContainer } from '../../container/user-module'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { transportTypes } from '../../common/enums/TransportType'
import { Notification as CalendarNotification } from '../../modules/calendar/models/Notification'
import style from './Editor.module.css'
import { IStatusService } from '../../common/status/StatusService'
import { cronString } from '../../modules/forms/enums/Cron'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { Input, InputTextDescription } from '../resource/userProfileStylesMui'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import TextField from '@mui/material/TextField'
import { EventCategory } from '../../modules/calendar/enums/EventCategory'
import { getAppContainer, STATUS_SERVICE_KEY } from 'container/app'
import { PageLayout } from 'pages/layout'

type EditorProps = {
  id?: string
  date?: Date
  currentUrl?: string
  eventCategory?: EventCategory
}

type AutocompleteUserInterface = {
  key: string
  value: string
}

const calendarContainer = getCalendarContainer()
const eventService = calendarContainer.get<EventService>(EVENT_SERVICE_KEY)

const loggedUserContainer = getUserContainer()
const loggedUserService = loggedUserContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

const notificationTypes = transportTypes()

const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export function Editor(props: EditorProps) {
  const { t } = useTranslation()

  const loggedUser = loggedUserService.get()
  const cronTypes = cronString()

  const [event, setEvent] = useState<EventDTO>(
    emptyEventDTO(props.date, loggedUser?.id || '', props.eventCategory)
  )

  const [isFinishReminder, setIsFinishReminder] = useState<boolean>(false)
  const [isRecurrence, setIsRecurrence] = useState<boolean>(false)
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [files, setFiles] = useState<F[]>([])
  const [cron, setCron] = useState<string>('0 0 * * *')
  const [startDateRec, setStartDateRec] = useState<Date>(new Date())
  const [finishDateRec, setFinishDateRec] = useState<Date>(new Date())
  const [hourNotValid, setHourNotValid] = useState<string | undefined>()
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const location = useLocation()

  const patientIdFromState = React.useMemo(() => {
    try {
      const st = location.state as any
      return st && typeof st === 'object' ? st.patientId ?? null : null
    } catch (e) {
      return null
    }
  }, [location.state])

  const patientIdFromUrl = React.useMemo(() => {
    const pathParts = location.pathname.split('/')
    const exercisesIndex = pathParts.indexOf('exercises')
    if (exercisesIndex !== -1 && pathParts[exercisesIndex + 1]) {
      return pathParts[exercisesIndex + 1]
    }
    return null
  }, [location.pathname])

  const patientId = patientIdFromState || patientIdFromUrl

  const handleChange = (s: string, sd: Date, fd: Date) => {
    setCron(s)
    setStartDateRec(sd)
    setFinishDateRec(fd)
  }

  useEffect(() => {
    if (!props.id) {
      if (loggedUser?.id) {
        setEvent((prev) => ({ ...prev, ownerID: loggedUser.id }))
      }
      if (props.eventCategory) {
        if (props.eventCategory !== undefined) {
          setEvent((prev) => ({ ...prev, eventCategory: props.eventCategory as EventCategory }))
        }
      }
      return
    }

    const sub = eventService.getByID(props.id).subscribe((res) => {
      if (!res) {
        return
      }

      const loadedEvent = fromModel(res)
      setEvent({
        ...loadedEvent,
        title: t(loadedEvent.title),
        description: t(loadedEvent.description),
        ownerID: loggedUser?.id || loadedEvent.ownerID,
      })

      if (res.repeatingUntil) {
        setIsFinishReminder(true)
      }
    })

    return () => {
      try {
        sub && sub.unsubscribe && sub.unsubscribe()
      } catch (e) {}
    }
  }, [props.id, loggedUser?.id, props.eventCategory])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setEvent(Object.assign({ ...event }, { [e.target.name]: e.target.value }))

  const handleDate = (e: any, name: string) => {
    setHourNotValid(undefined)
    if (!e) return
    let hours = e?.hour()
    if (!hours) return
    if (hours < 7 && e) {
      setHourNotValid('La hora debe ser mayor o igual a las 7:00')
      return
    } else if (hours > 22 && e) {
      setHourNotValid('La hora debe ser menor o igual a las 22:00')
      return
    }

    e && setEvent(Object.assign({ ...event }, { [name]: e.toDate() }))
  }

  const handleDateRec = (startDateRec: Date, finishDateRec: Date) => {
    setEvent(Object.assign({ ...event }, { startCron: startDateRec, finishCron: finishDateRec }))
  }

  const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'finishReminder':
        setIsFinishReminder(!isFinishReminder)
        break
      case 'recurrence':
        setIsRecurrence(!isRecurrence)
        break
      default:
        break
    }
  }

  const handleNotification = (notifications: CalendarNotification[]) =>
    setEvent(Object.assign({ ...event }, { notifications: notifications.map((n) => n.id) }))

  const saveEvent = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    let editCalentar = document.getElementById('EditcalendarEvent') as HTMLFormElement
    if (!editCalentar.checkValidity()) {
      editCalentar.reportValidity()
      return
    }

    const newEvent = Object.assign(
      { ...event },
      {
        users: [loggedUser?.id],
        repeatingUntil: isFinishReminder ? event.repeatingUntil : undefined,
        repeating: cron,
        startCron: startDateRec,
        finishCron: finishDateRec,
      }
    )

    if (props.id) {
      eventService.update(newEvent).subscribe(() => {
        if (patientId) {
          navigate(`/calendar/exercises/${patientId}`, { state: { selectedDate: props.date } })
        } else {
          goBack()
        }
      })
    } else {
      eventService.add(newEvent).subscribe(() => {
        if (patientId) {
          navigate(`/calendar/exercises/${patientId}`, { state: { selectedDate: props.date } })
        } else {
          goBack()
        }
      })
    }
  }

  const goBack = () => {
    if (props.currentUrl === ROUTE_NEWS_WALL) {
      navigate(ROUTE_NEWS_WALL)
      return
    }

    if (patientId) {
      navigate(`/calendar/exercises/${patientId}`, { state: { selectedDate: props.date } })
      return
    }

    try {
      const ref = document.referrer || ''
      const m = ref.match(/\/calendar\/exercises\/([^\/?#]+)/)
      if (m && m[1]) {
        navigate(`/calendar/exercises/${m[1]}`, { state: { selectedDate: props.date } })
        return
      }
    } catch (e) {}

    if (window.history && window.history.length > 1) {
      window.history.back()
      return
    }

    navigate(ROUTE_CALENDAR, { state: { selectedDate: props.date } })
  }

  return (
    <PageLayout>
      <form className={style.form} id={'EditcalendarEvent'} onSubmit={(e) => saveEvent(e)}>
        <Box
          mb={3}
          className={style.textField}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
        >
          <p className={style.label}>{`${t('title')}*`}</p>
          <Input
            key={'title'}
            id={'title'}
            name={'title'}
            value={event.title}
            onChange={handleInput}
            required={true}
            size={'small'}
            variant={'outlined'}
            fullWidth={true}
          />
        </Box>

        <Box
          mb={3}
          className={style.textField}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
        >
          <p className={style.label}>{`${t('description')}*`}</p>
          <InputTextDescription
            fullWidth
            id="description"
            name={'description'}
            value={event.description}
            label={''}
            type={'text'}
            onChange={handleInput}
            rows={10}
            required={true}
          />
        </Box>
        <Box
          mb={3}
          display={'flex'}
          justifyContent={'space-between'}
          className={style.datePickerContainer}
        >
          <Box width={'100%'}>
            <Box display={'flex'} alignItems={'center'} className={style.datePicker}>
              <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                {t('startDate')}
              </p>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DateTimePicker
                  key={'startDate'}
                  ampm={false}
                  minDate={props.date ?? new Date()}
                  inputFormat={'DD/MM/YYYY HH:mm'}
                  onChange={(e) => handleDate(e, 'startDate')}
                  value={event.startDate}
                  label={''}
                  onError={(reason) => {
                    switch (reason) {
                      case 'invalidDate':
                        setDateTimePickerError(t('invalidDateMessage'))
                        break
                      case 'minDate':
                        setDateTimePickerError(t('minDateMessage'))
                        break
                    }
                  }}
                  renderInput={(props) => (
                    <TextField
                      sx={{
                        '& fieldset': {
                          borderRadius: 32,
                          border: '1px solid #E8E7EC',
                        },
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '&.Mui-focused fieldset': {
                            borderColor: 'var(--green-light)',
                          },
                        },
                      }}
                      size={'small'}
                      helperText={props.error && DateTimePickerError}
                      {...props}
                    />
                  )}
                  components={{
                    OpenPickerIcon: () => (
                      <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Box width={'100%'}>
            <Box display={'flex'} alignItems={'center'} className={style.datePicker2}>
              <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
                {t('finishDate')}
              </p>

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DateTimePicker
                  key={'finishDate'}
                  ampm={false}
                  minDate={event.startDate}
                  inputFormat={'DD/MM/YYYY HH:mm'}
                  onChange={(e) => handleDate(e, 'finishDate')}
                  value={event.finishDate}
                  label={''}
                  onError={(reason) => {
                    switch (reason) {
                      case 'invalidDate':
                        setDateTimePickerError(t('invalidDateMessage'))
                        break
                      case 'minDate':
                        setDateTimePickerError(t('minDateMessage'))
                        break
                    }
                  }}
                  renderInput={(props) => (
                    <TextField
                      sx={{
                        '& fieldset': {
                          borderRadius: 32,
                          border: '1px solid #E8E7EC',
                        },
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '&.Mui-focused fieldset': {
                            borderColor: 'var(--green-light)',
                          },
                        },
                      }}
                      size={'small'}
                      helperText={props.error && DateTimePickerError}
                      {...props}
                    />
                  )}
                  components={{
                    OpenPickerIcon: () => (
                      <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
        <Box mb={3}>
          <Box display={'flex'} alignItems={'center'} width={'100%'} mb={3}>
            <Box display={'flex'} alignItems={'center'} width={'100%'}>
              <h4 style={{ display: 'inline', marginBottom: 0 }}>{t('includeEndDateReminder')}</h4>
              <Checkbox
                name={'finishReminder'}
                checked={isFinishReminder}
                onChange={handleCheckbox}
              />
            </Box>
            <Box
              className={style.textField}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              width={'100%'}
            >
              <p style={{ marginBottom: 0 }} className={style.label}>{`${t('place')}`}</p>
              <Input
                key={'place'}
                id={'place'}
                name={'place'}
                value={event.place}
                onChange={handleInput}
                size={'small'}
                variant={'outlined'}
                fullWidth={true}
              />
            </Box>
          </Box>
          <Box className={style.datePicker} display={'flex'} alignItems={'center'}>
            <p style={{ marginBottom: 0, marginRight: 24 }} className={style.label}>
              {t('startDate')}
            </p>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
              <DateTimePicker
                key={'repeatingUntil'}
                ampm={false}
                inputFormat={'DD/MM/YYYY HH:mm'}
                onChange={(e) => handleDate(e, 'repeatingUntil')}
                value={event.repeatingUntil}
                label={''}
                disabled={!isFinishReminder}
                onError={(reason) => {
                  switch (reason) {
                    case 'invalidDate':
                      setDateTimePickerError(t('invalidDateMessage'))
                      break
                    case 'minDate':
                      setDateTimePickerError(t('minDateMessage'))
                      break
                  }
                }}
                renderInput={(props) => (
                  <TextField
                    id={'repeatingUntil'}
                    sx={{
                      '& fieldset': {
                        borderRadius: 32,
                        border: '1px solid #E8E7EC',
                      },
                      '& .MuiOutlinedInput-root': {
                        border: 'none',
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--green-light)',
                        },
                      },
                    }}
                    size={'small'}
                    helperText={props.error && DateTimePickerError}
                    {...props}
                  />
                )}
                components={{
                  OpenPickerIcon: () => (
                    <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                  ),
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box mb={3}>
          <p className={style.recurrenceLabel}>{t('recurrence')}</p>
          <Checkbox
            sx={{
              [`&, &.${checkboxClasses.checked}`]: {
                color: 'var(--green-light)',
              },
            }}
            checked={isRecurrence}
            name={'recurrence'}
            onChange={handleCheckbox}
          />
        </Box>
        {isRecurrence && (
          <Box mb={3}>
            <CronItemMockup handleChange={handleChange} />
          </Box>
        )}
        {/*<Box mb={3} display={'flex'} className={style.textField}>
              <p className={style.label + ' ' + style.usersLabel}>{t('users')}</p>
              <Box style={{ display: 'inline' }}>
                <Autocomplete
                  className={style.autocomplete}
                  key={event.id}
                  id={`${event.id}-standard`}
                  value={[]}
                  multiple={true}
                  options={
                    Array.from(users.values()).filter(
                      (u) => !selectedUsers.includes(u)
                    ) as AutocompleteUserInterface[]
                  }
                  getOptionLabel={(opt) => t(opt.value)}
                  onChange={(_, v) => handleUsers(v)}
                  renderInput={(params) => <TextField {...params} variant="outlined" />}
                  noOptionsText={t('noElementsAvailable')}
                  size={'small'}
                />
              </Box>
              {selectedUsers.length > 0 && (
                <Box display={'inline'}>
                  {selectedUsers.map((u) => (
                    <Chip
                      className={style.chip}
                      label={u.value}
                      onDelete={() => removeUser(u.value)}
                    />
                  ))}
                </Box>
              )}
            </Box>*/}
        {/*    <Box mb={3} className={style.textField}>
              <p className={style.notificationLabel}>{t('addNotification')}</p>
              {Object.keys(notificationTypes).map((value) => (
                <Select
                  type={notificationTypes[(value as unknown) as TransportType]}
                  key={notificationTypes[(value as unknown) as TransportType]}
                  value={notificationTypes[(value as unknown) as TransportType]}
                  notifications={event.notifications}
                  handlerChange={handleNotification}
                  cleanNotifications={false}
                />
              ))}
            </Box>*/}
        <Box display="flex" justifyContent="flex-end">
          <Box mr={4}>
            <AppButton
              theme={ButtonTheme.NewSecondary}
              type={'button'}
              label={t('cancel')}
              handler={goBack}
            />
          </Box>
          <AppButton
            theme={ButtonTheme.NewPrimary}
            type={'submit'}
            label={t('save')}
            handler={saveEvent}
          />
        </Box>
      </form>
    </PageLayout>
  )
}
